// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Box, Button, Group, Select, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCheck } from '@tabler/icons-react';
import { type JSX, useEffect, useState } from 'react';

import { FormCard } from '../../../components/FormCard';
import { useRole } from '../../../hooks/useRole';
import { getExtensionValue, getTelecom, TIMEZONE_EXTENSION } from '../../../utils/practitioner';
import { ACCOUNT_TYPE_OPTIONS, TIMEZONE_OPTIONS } from '../constants';

interface SystemSettingsSectionProps {
  practitioner: Practitioner;
  onSaved: (updated: Practitioner) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
}

export function SystemSettingsSection({
  practitioner,
  onSaved,
  isEditing,
  onStartEdit,
  onEndEdit,
}: SystemSettingsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const { role } = useRole();
  const [saving, setSaving] = useState(false);

  const form = useForm({
    validate: {
      zoomEmail: (value) => (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Enter a valid email address' : null),
    },
    initialValues: {
      accountType: '',
      zoomEmail:
        practitioner.telecom?.find((t) => t.system === 'url' && t.use === 'work')?.value ??
        getTelecom(practitioner, 'email'),
      timezone: getExtensionValue(practitioner, TIMEZONE_EXTENSION),
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: form.setFieldValue is stable
  useEffect(() => {
    if (role) {
      form.setFieldValue('accountType', role);
    }
  }, [role]);

  const handleSave = form.onSubmit(async (values: typeof form.values) => {
    setSaving(true);
    try {
      const updated: Practitioner = {
        ...practitioner,
        telecom: [
          ...(practitioner.telecom?.filter((t) => !(t.system === 'url' && t.use === 'work')) ?? []),
          ...(values.zoomEmail ? [{ system: 'url' as const, use: 'work' as const, value: values.zoomEmail }] : []),
        ],
        extension: [
          ...(practitioner.extension?.filter((e) => e.url !== TIMEZONE_EXTENSION) ?? []),
          ...(values.timezone ? [{ url: TIMEZONE_EXTENSION, valueString: values.timezone }] : []),
        ],
      };
      const result = await medplum.updateResource(updated);
      onSaved(result);
      onEndEdit();
      showNotification({
        color: 'green',
        icon: <IconCheck size={16} />,
        message: 'System settings updated',
      });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  return (
    <FormCard
      title="System Settings"
      isEditing={isEditing}
      action={
        isEditing ? (
          <Group gap="xs">
            <Button
              size="xs"
              variant="default"
              onClick={() => {
                form.reset();
                onEndEdit();
              }}
            >
              Cancel
            </Button>
            <Button size="xs" onClick={() => handleSave()} loading={saving}>
              Save
            </Button>
          </Group>
        ) : (
          <Button size="xs" onClick={onStartEdit}>
            Edit
          </Button>
        )
      }
    >
      <Stack gap="sm">
        <Group grow>
          <Select label="Account type" data={ACCOUNT_TYPE_OPTIONS} {...form.getInputProps('accountType')} disabled />
          <TextInput
            label="Zoom account email"
            placeholder="name@example.com"
            disabled={!isEditing}
            {...form.getInputProps('zoomEmail')}
          />
        </Group>
        <Group grow>
          <Select label="Timezone" data={TIMEZONE_OPTIONS} disabled={!isEditing} {...form.getInputProps('timezone')} />
          <Box />
        </Group>
      </Stack>
    </FormCard>
  );
}
